import { Link } from "react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { motion } from "motion/react";
import { Seo } from "../components/Seo";
import { allCityLandingPages, getCityLandingPath, getCityProfile } from "../local-pages";
import { getAbsoluteUrl, getSiteOrigin } from "../site";

export function Standorte() {
  const locationCards = allCityLandingPages.map((entry) => {
    const profile = getCityProfile(entry.citySlug);
    const href = getCityLandingPath(entry.citySlug, entry.variant);
    const label =
      entry.variant === "website-erstellen-lassen"
        ? `Website erstellen lassen ${entry.city}`
        : `Webdesign ${entry.city}`;

    return {
      city: entry.city,
      href,
      label,
      regionLabel: profile?.regionLabel ?? entry.city,
      audienceSummary: profile?.audienceSummary ?? "",
      nearbyAreas: profile?.nearbyAreas ?? [],
    };
  });

  return (
    <div className="bg-stone-50 pt-20 text-gray-950">
      <Seo
        title="Standorte"
        description="Lokale Landingpages von mutzur für Webdesign und Website-Erstellung in Dresden, Senftenberg, Hoyerswerda, Meißen, Ruhland und Schwarzheide."
        path="/standorte"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Standorte",
            description:
              "Übersicht der lokalen Landingpages von mutzur für Webdesign und Website-Erstellung.",
            url: `${getSiteOrigin()}/standorte`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Start",
                item: getAbsoluteUrl("/"),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Standorte",
                item: getAbsoluteUrl("/standorte"),
              },
            ],
          },
        ]}
      />

      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="hover:text-black">
                  Start
                </Link>
              </li>
              <li>/</li>
              <li className="text-gray-900">Standorte</li>
            </ol>
          </nav>

          <p className="mt-8 text-sm font-medium uppercase tracking-[0.18em] text-gray-500">
            Lokale Landingpages
          </p>
          <h1 className="mt-4 max-w-4xl text-4xl leading-tight lg:text-5xl">
            Webdesign und Website-Erstellung für Unternehmen in Ihrer Region
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-gray-700 sm:text-lg">
            Die lokalen Seiten sind nicht als austauschbare SEO-Flächen gedacht, sondern als
            Einstieg für Unternehmen mit regionalem Fokus. Jede Seite erklärt, für wen das Modell
            vor Ort sinnvoll ist, welche Anforderungen typisch sind und wie die Zusammenarbeit
            abläuft.
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          {locationCards.map((card, index) => (
            <motion.article
              key={`${card.href}-${card.label}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.05 }}
              className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <MapPin className="h-4 w-4" />
                <span>{card.regionLabel}</span>
              </div>
              <h2 className="mt-5 text-2xl leading-tight text-gray-950">{card.label}</h2>
              <p className="mt-4 text-base leading-relaxed text-gray-700">
                {card.audienceSummary}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {card.nearbyAreas.map((area) => (
                  <span
                    key={area}
                    className="rounded-full bg-stone-100 px-3 py-1 text-sm text-gray-700"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <Link
                to={card.href}
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-black"
              >
                Seite ansehen
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
